import { BookingPriceQuote } from "../../model/bookings/BookingPriceQuote";
import { CreateBookingRequest } from "../../model/bookings/CreateBookingRequest";
import { CancelBookingRequest } from "../../model/bookings/CancelBookingRequest";
import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import { FacilityTypeContext } from "../../model/facilities/FacilityTypeContext";
import { Facility } from "../../model/facilities/Facility";
import { FacilityBookingsDay } from "../../model/bookings/FacilityBookingsDay";

export class BookingService extends ClubBookingApiClient {
    async getBookings(
        from: Date,
        to: Date,
        facilityType: FacilityTypeContext
    ): Promise<FacilityBookingsDay[]> {
        const data = await this.get<FacilityBookingsDay[]>(
            `/bookings?from=${from.toDateString()}&to=${to.toDateString()}&facilityType=${facilityType.facilityType}`,
            undefined,
            { skipClientHeader: false }
        );
        return data.map(day => ({
            ...day,
            facilities: [...day.facilities].sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            ),
        }));
    }

    async saveBooking(bookingRequest: CreateBookingRequest): Promise<void> {
        try {
            await this.post('/bookings/create', bookingRequest, { successMessage: 'Booking created successfully' });
        } catch (error) {
            console.error('Error saving booking:', error);
            throw error;
        }
    }

    async getFacilityTypes(): Promise<FacilityTypeContext[]> {
        return this.get<FacilityTypeContext[]>('/facilities/types');
    }

    // The API's price for a booking of `facilityName` from start to end (FORT-288).
    // No global spinner: the booking modal re-quotes as the length changes. Resolves
    // undefined, silently, when the quote fails — callers show the price as unavailable.
    async getPriceQuote(facilityName: string, start: Date, end: Date): Promise<BookingPriceQuote | undefined> {
        return this.get<BookingPriceQuote>(
            '/bookings/price-quote',
            { facilityName, start: start.toISOString(), end: end.toISOString() },
            { showLoading: false }
        );
    }

    async getFacility(facilityName: string): Promise<Facility> {
        return this.get<Facility>(`/facilities/${facilityName}`);
    }

    async cancelBooking(cancelBookingRequest: CancelBookingRequest): Promise<void> {
        try {
            await this.post('/bookings/cancel', cancelBookingRequest, { successMessage: 'Booking cancelled successfully' });
        } catch (error) {
            console.error('Error cancelling booking:', error);
            throw error;
        }
    }
}
